import { useMemo } from "react"
import { getTailwindValue } from "~/utils/tailwind"
import { useHakiChartColors } from "~/constants/colors"

/**
 * Theme-reactive id→color map for entity series (warehouses, storage locations,
 * channels): cycles the haki chart palette in item order.
 */
export function useIdColorMap(items: { id: string }[] | undefined): Map<string, string> {
  const colors = useHakiChartColors()
  return useMemo(
    () => new Map((items ?? []).map((item, idx) => [item.id, colors[idx % colors.length] ?? ""])),
    [items, colors],
  )
}

export function generateDistinctColors(count: number): string[] {
  const distinctColorOptions = [
    getTailwindValue("--color-green-500"),
    getTailwindValue("--color-violet-500"),
    getTailwindValue("--color-orange-500"),
    getTailwindValue("--color-red-500"),
    getTailwindValue("--color-blue-500"),
    getTailwindValue("--color-mint-500"),
    getTailwindValue("--color-lavander-500"),
    getTailwindValue("--color-yellow-500"),
    getTailwindValue("--color-gray-500"),
    getTailwindValue("--color-green-700"),
    getTailwindValue("--color-violet-700"),
    getTailwindValue("--color-orange-700"),
  ]

  return Array.from({ length: count }, (_, i) => {
    return distinctColorOptions[i % distinctColorOptions.length] ?? "#476AE7"
  })
}

/**
 * Get availability background color classes based on percentage value
 * @param value - Availability percentage (0-100)
 * @returns Object with text600, background500 and background100 color classes
 */
export function getAvailabilityColors(value: number | undefined): {
  text600: string
  background500: string
  background100: string
  border: string
} {
  if (value === undefined || value === 0) {
    return {
      text600: "text-badge-neutral-fg-text",
      background500: "bg-badge-status-sqaure-neutral",
      background100: "bg-badge-neutral-bg",
      border: "border border-badge-neutral-border",
    }
  }

  if (value <= 50) {
    return {
      text600: "text-badge-red-fg-text",
      background500: "bg-badge-status-sqaure-error",
      background100: "bg-badge-red-bg",
      border: "border border-badge-red-border",
    }
  }

  if (value <= 90) {
    return {
      text600: "text-badge-orange-fg-text",
      background500: "bg-icon-orange",
      background100: "bg-badge-orange-bg",
      border: "border border-badge-orange-border",
    }
  }

  return {
    text600: "text-badge-green-fg-text",
    background500: "bg-badge-status-sqaure-success",
    background100: "bg-badge-green-bg",
    border: "border border-badge-green-border",
  }
}
